import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ScheduleItem {
  date: string;
  weekday: string;
  time: string;
  city: string;
  venue: string;
  seats: number;
  totalSeats: number;
  isFull: boolean;
}

const upcomingSchedules: ScheduleItem[] = [
  {
    date: "03/15",
    weekday: "週六",
    time: "14:00–16:00",
    city: "台北",
    venue: "萬華服務據點｜台北市萬華區漢中街195號",
    seats: 8,
    totalSeats: 20,
    isFull: false,
  },
  {
    date: "03/22",
    weekday: "週六",
    time: "14:00–16:00",
    city: "新竹",
    venue: "竹北服務據點｜新竹縣竹北市自強南路372號3樓",
    seats: 12,
    totalSeats: 20,
    isFull: false,
  },
  {
    date: "04/05",
    weekday: "週六",
    time: "10:00–12:00",
    city: "台中",
    venue: "台中設計總部｜台中市北區中清路一段79號6樓",
    seats: 3,
    totalSeats: 25,
    isFull: false,
  },
  {
    date: "04/12",
    weekday: "週六",
    time: "14:00–16:00",
    city: "台南",
    venue: "永康服務據點｜臺南市永康區中華路12號13樓之3",
    seats: 15,
    totalSeats: 20,
    isFull: false,
  },
  {
    date: "04/26",
    weekday: "週六",
    time: "14:00–16:00",
    city: "屏東",
    venue: "九如服務據點｜屏東縣九如鄉仁愛街45巷19號1樓",
    seats: 0,
    totalSeats: 15,
    isFull: true,
  },
];

function getSeatColor(seats: number, total: number, isFull: boolean) {
  if (isFull || seats === 0) return "bg-medium-gray text-white";
  const ratio = seats / total;
  if (ratio <= 0.2) return "bg-red-500 text-white";
  if (ratio <= 0.5) return "bg-orange-400 text-white";
  return "bg-emerald-600 text-white";
}

function parseScheduleDate(dateStr: string, timeStr: string): Date | null {
  const now = new Date();
  const year = now.getFullYear();
  const [month, day] = dateStr.split("/").map(Number);
  if (!month || !day) return null;

  const [timePart] = timeStr.split("–");
  const [hourStr] = timePart.split(":");
  const hour = parseInt(hourStr, 10) || 14;

  const d = new Date(year, month - 1, day, hour, 0, 0);
  if (d < now && d.getTime() < now.getTime() - 86400000) {
    // If passed by more than a day, assume next year
    return new Date(year + 1, month - 1, day, hour, 0, 0);
  }
  return d;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "00:00:00:00";
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${String(days).padStart(2, "0")}:${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function SeminarScheduleSection() {
  const { t } = useTranslation();
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [countdown, setCountdown] = useState("");
  const [nearestEvent, setNearestEvent] = useState<{ item: ScheduleItem; date: Date } | null>(null);

  const findNearestEvent = useCallback(() => {
    const now = new Date().getTime();
    let nearest: { item: ScheduleItem; date: Date; diff: number } | null = null;

    for (const item of upcomingSchedules) {
      const d = parseScheduleDate(item.date, item.time);
      if (!d) continue;
      const diff = d.getTime() - now;
      if (diff > 0 && (!nearest || diff < nearest.diff)) {
        nearest = { item, date: d, diff };
      }
    }

    if (nearest) {
      setNearestEvent({ item: nearest.item, date: nearest.date });
    } else {
      setNearestEvent(null);
    }
  }, []);

  useEffect(() => {
    findNearestEvent();
    const interval = setInterval(() => {
      findNearestEvent();
    }, 60000); // recheck nearest every minute
    return () => clearInterval(interval);
  }, [findNearestEvent]);

  useEffect(() => {
    if (!nearestEvent) {
      setCountdown("");
      return;
    }
    const tick = () => {
      const diff = nearestEvent.date.getTime() - new Date().getTime();
      if (diff <= 0) {
        setCountdown("00:00:00:00");
        findNearestEvent();
        return;
      }
      setCountdown(formatCountdown(diff));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [nearestEvent, findNearestEvent]);

  return (
    <section className="section-padding bg-cream">
      <div className="container-main px-4 md:px-8 lg:px-12">
        <div className="text-center mb-10 md:mb-14">
          <span className="tag-label mb-4 inline-block">{t("seminars.schedule.label")}</span>
          <h2 className="text-graphite">{t("seminars.schedule.title")}</h2>
          <p className="text-medium-gray mt-3 max-w-xl mx-auto">
            {t("seminars.schedule.subtitle")}
          </p>
        </div>

        {/* Countdown Banner */}
        {nearestEvent && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-graphite rounded-2xl p-6 md:p-8 text-center border border-white/10">
              <p className="text-cream/60 text-sm mb-2">
                距離下一場講座開始還有
              </p>
              <div className="flex items-center justify-center gap-2 md:gap-4 mb-3">
                {countdown.split(":").map((val, i) => {
                  const labels = ["天", "時", "分", "秒"];
                  return (
                    <div key={i} className="text-center">
                      <div className="bg-white/10 rounded-xl px-3 py-2 md:px-4 md:py-3 min-w-[56px] md:min-w-[72px]">
                        <span className="text-champagne font-serif font-bold text-2xl md:text-4xl tabular-nums">
                          {val}
                        </span>
                      </div>
                      <p className="text-cream/50 text-xs mt-1">{labels[i]}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-2 text-cream/80 text-sm">
                <span className="w-4 h-4 flex items-center justify-center text-champagne">
                  <i className="ri-map-pin-line" />
                </span>
                {nearestEvent.item.city} 場次 ｜ {nearestEvent.item.date} {nearestEvent.item.weekday} {nearestEvent.item.time}
                {nearestEvent.item.seats > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-champagne/15 text-champagne text-xs font-semibold"
                  >
                    剩餘 {nearestEvent.item.seats} 席
                  </span>
                )}
                {nearestEvent.item.isFull && (
                  <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-medium-gray/30 text-cream/70 text-xs font-semibold"
                  >
                    已額滿
                  </span>
                )}
              </div>
              <a
                href="https://lin.ee/OpssTIM"
                target="_blank"
                rel="noopener noreferrer"
                className={`btn-primary mt-4 gap-2 text-base ${
                  nearestEvent.item.isFull ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-line-fill" />
                </span>
                {nearestEvent.item.isFull ? "候補登記" : "立即報名這場講座"}
              </a>
            </div>
          </div>
        )}

        {/* Schedule cards */}
        <div className="flex flex-col gap-4 max-w-4xl mx-auto">
          {upcomingSchedules.map((item, i) => {
            const isExpanded = expandedIdx === i;
            const seatPct = Math.round((item.seats / item.totalSeats) * 100);
            return (
              <div
                key={i}
                className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
                  isExpanded
                    ? "border-champagne/50 ring-1 ring-champagne/20"
                    : "border-warm-gray/50 hover:border-champagne/40"
                }`}
              >
                <button
                  onClick={() => setExpandedIdx(isExpanded ? null : i)}
                  className="w-full flex items-center gap-3 md:gap-5 px-5 md:px-7 py-5 text-left cursor-pointer"
                >
                  <div className="shrink-0 text-center min-w-[3.5rem]">
                    <p className="text-graphite font-bold text-xl md:text-2xl">{item.date}</p>
                    <p className="text-medium-gray text-sm">{item.weekday}</p>
                  </div>

                  <div className="w-px h-12 bg-warm-gray/60 hidden sm:block" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-5 h-5 flex items-center justify-center text-champagne shrink-0">
                        <i className="ri-map-pin-line" />
                      </span>
                      <span className="text-graphite font-semibold text-base truncate">
                        {item.city}
                      </span>
                      <span className="text-medium-gray text-sm hidden sm:inline">
                        {item.time}
                      </span>
                    </div>
                    <p className="text-medium-gray text-sm truncate hidden md:block">
                      {item.venue}
                    </p>
                  </div>

                  <span
                    className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${getSeatColor(
                      item.seats,
                      item.totalSeats,
                      item.isFull
                    )}`}
                  >
                    {item.isFull ? "已額滿" : `剩餘 ${item.seats} 席`}
                  </span>

                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-full bg-cream text-graphite shrink-0 transition-transform duration-300 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  >
                    <i className="ri-arrow-down-s-line" />
                  </span>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isExpanded ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-5 md:px-7 pb-6 pt-0 border-t border-warm-gray/30">
                    <div className="pt-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <p className="text-graphite font-medium mb-1">
                          <span className="text-medium-gray font-normal mr-2">場地</span>
                          {item.venue}
                        </p>
                        <p className="text-graphite font-medium mb-1">
                          <span className="text-medium-gray font-normal mr-2">時間</span>
                          {item.weekday} {item.time}
                        </p>
                        <div className="mt-3 flex items-center gap-3">
                          <div className="flex-1 h-2 bg-warm-gray/50 rounded-full overflow-hidden max-w-[200px]">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                item.isFull
                                  ? "bg-medium-gray"
                                  : seatPct <= 20
                                    ? "bg-red-500"
                                    : seatPct <= 50
                                      ? "bg-orange-400"
                                      : "bg-emerald-600"
                              }`}
                              style={{ width: `${100 - seatPct}%` }}
                            />
                          </div>
                          <span className="text-sm text-medium-gray">
                            {item.totalSeats - item.seats} / {item.totalSeats} 已報名
                          </span>
                        </div>
                      </div>

                      <a
                        href="https://lin.ee/OpssTIM"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`btn-primary shrink-0 gap-2 text-base whitespace-nowrap ${
                          item.isFull ? "opacity-50 pointer-events-none" : ""
                        }`}
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-line-fill" />
                        </span>
                        {item.isFull ? "候補登記" : "立即報名"}
                      </a>
                    </div>

                    <div className="mt-4 bg-cream rounded-xl p-4 flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center text-champagne shrink-0 mt-0.5">
                        <i className="ri-information-line" />
                      </span>
                      <p className="text-medium-gray text-sm leading-relaxed">
                        報名後將由專人與您聯繫確認場次與報到資訊。若臨時無法出席，請提前 3 天告知。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-10">
          <p className="text-medium-gray text-base mb-4">
            沒有適合的場次？我們也可以安排線上說明會或一對一諮詢。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://lin.ee/OpssTIM"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary gap-2 text-base"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-line-fill" />
              </span>
              加入 LINE 預約其他場次
            </a>
            <Link
              to="/contact"
              className="btn-outline border-warm-gray/60 text-graphite hover:border-champagne hover:text-champagne gap-2 text-base"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-check-line" />
              </span>
              預約一對一諮詢
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}